import { Dialog, DialogTitle, DialogContent, Box, IconButton, Divider, useTheme } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ValidationMessage, ValidationSeverity } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { Link, useParams, useLocation } from "react-router";
import { resolveUrlForProperty } from "src/utils/useQuestionnaireNavigation.ts";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

type ValidationErrorsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    validationMessages: ValidationMessage[];
    context?: {
        ssqVersion?: SsqVersion;
    };
};

export const ValidationErrorsModal = (props: ValidationErrorsModalProps) => {
    const { isOpen, onClose, validationMessages, context } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const severityConfig = {
        error: {
            badgeBgColor: theme.custom.red[50],
            badgeTextColor: theme.custom.red[600],
            iconName: "cancel" as const,
            iconColor: theme.custom.red[600],
            borderColor: theme.custom.red[600],
            badgeLabel: t("validation.errors.error_badge", "Errors"),
            description: t(
                "validation.errors.error_title",
                "Your questionnaire could not be validated due to the following errors",
            ),
        },
        warning: {
            badgeBgColor: theme.custom.orange[50],
            badgeTextColor: theme.custom.orange[600],
            iconName: "warning" as const,
            iconColor: theme.custom.orange[600],
            borderColor: theme.custom.orange[600],
            badgeLabel: t("validation.errors.warning_badge", "Alerts"),
            description: t(
                "validation.errors.warning_title",
                "The following issues have been identified in your questionnaire without blocking its validation",
            ),
        },
        warning_with_block: {
            badgeBgColor: theme.custom.orange[50],
            badgeTextColor: theme.custom.orange[600],
            iconName: "warning" as const,
            iconColor: theme.custom.orange[600],
            borderColor: theme.custom.orange[600],
            badgeLabel: t("validation.errors.warning_block_badge", "Blocking alerts"),
            description: t(
                "validation.errors.warning_block_title",
                "The following issues have been identified in your questionnaire that block its validation",
            ),
        },
    };

    const params = useParams();
    const location = useLocation();

    const errorMessages = validationMessages.filter((msg) => msg.severity === "error");
    const warningMessages = validationMessages.filter((msg) => msg.severity === "warning");
    const blockingWarningMessages = validationMessages.filter((msg) => msg.severity === "warning_with_block");

    const renderValidationSection = (messages: ValidationMessage[], severity: ValidationSeverity) => {
        if (messages.length === 0) return null;

        const config = severityConfig[severity];

        return (
            <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                            backgroundColor: config.badgeBgColor,
                            borderRadius: "4px",
                            px: 1,
                            py: 0.25,
                            height: "26px",
                            width: "fit-content",
                        }}
                    >
                        <Box sx={{ color: config.iconColor, display: "flex", alignItems: "center" }}>
                            <FoodsIcon icon={config.iconName} size={1.5} color="inherit" />
                        </Box>
                        <Typography
                            variant="body-medium"
                            sx={{ fontWeight: 700, fontSize: 14, color: config.badgeTextColor }}
                        >
                            {config.badgeLabel}
                        </Typography>
                    </Box>
                    <Typography variant="body-medium" sx={{ fontWeight: 700, fontSize: 14 }}>
                        {config.description}:
                    </Typography>
                </Box>

                {messages.map((validationMessage, index) => {
                    const propertyName = validationMessage.hint?.propertyName;
                    const questionnaireLink = resolveUrlForProperty(params, validationMessage, location.pathname);

                    const validationMessageI18nKey = validationMessage.hint?.i18nKey;
                    const localizedValidationMessage = t(validationMessageI18nKey || "", {
                        defaultValue: validationMessage.message,
                        rate: context?.ssqVersion?.ssq.minimumCompletionRate,
                    });

                    return (
                        <Box
                            key={`${severity}-${index}`}
                            display="flex"
                            alignItems="center"
                            sx={{
                                borderLeft: `2px solid ${config.borderColor}`,
                                pl: 2,
                            }}
                        >
                            <Box flex={1} display="flex" flexDirection="column">
                                {propertyName && (
                                    <Typography variant="body-medium" sx={{ fontWeight: 600, fontSize: 14 }}>
                                        {propertyName}
                                    </Typography>
                                )}
                                <Typography variant="body-medium" sx={{ fontWeight: 300, fontSize: 14 }}>
                                    {localizedValidationMessage}
                                </Typography>
                            </Box>
                            {questionnaireLink !== null && (
                                <Link to={questionnaireLink} target="_blank">
                                    <FoodsIconButton
                                        size={"xs"}
                                        icon={"openNewTab"}
                                        tooltip={{
                                            text: t(
                                                "validation.errors.correct_tooltip",
                                                "Click to correct this in the questionnaire.",
                                            ),
                                        }}
                                    />
                                </Link>
                            )}
                        </Box>
                    );
                })}

                <Divider sx={{ borderColor: "#E0DEDD" }} />
            </Box>
        );
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: "16px" }}>
                        {t("validation.errors.modal_title", "Errors & warnings")}
                    </Typography>
                    <IconButton onClick={onClose} edge="end" size="small" aria-label="close" sx={{ p: 1 }}>
                        <Close sx={{ fontSize: 28 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
                <Box display="flex" flexDirection="column" gap={4}>
                    {errorMessages.length > 0 && renderValidationSection(errorMessages, "error")}

                    {blockingWarningMessages.length > 0 &&
                        renderValidationSection(blockingWarningMessages, "warning_with_block")}

                    {warningMessages.length > 0 && renderValidationSection(warningMessages, "warning")}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
